/**
 * performance-monitor.js
 * Módulo para monitorar o desempenho de operações críticas no SIE
 * Permite medir o tempo de execução, uso de memória e número de renderizações
 */

class PerformanceMonitor {
  constructor() {
    this.measurements = {};
    this.renderCounts = {};
    this.startTimes = {};
    this.memorySnapshots = {};
    this.enabled = true;
    this._initialized = false;
    this._activeRenders = {};
    
    // Configurações padrão
    this.config = {
      captureAllOperations: false,
      captureAllRenders: false,
      captureFirestoreOperations: false,
      detailedLogging: false
    };
    
    // Inicializa com o timestamp de início
    this.initTime = Date.now();
  }
  
  /**
   * Define as configurações de monitoramento
   * @param {Object} config - Configurações para o monitor
   * @param {boolean} config.captureAllOperations - Captura todas as operações, mesmo as menores
   * @param {boolean} config.captureAllRenders - Captura todas as renderizações de componentes
   * @param {boolean} config.captureFirestoreOperations - Monitora operações específicas do Firestore
   * @param {boolean} config.detailedLogging - Ativa logs detalhados para depuração
   */
  setConfig(config = {}) {
    this.config = {
      ...this.config,
      ...config
    };
    console.log('🛠️ [PerformanceMonitor] Configuração atualizada:', this.config);
  }
  
  /**
   * Ativa ou desativa o monitoramento de desempenho
   * @param {boolean} isEnabled - Se o monitoramento está habilitado
   */
  setEnabled(isEnabled) {
    this.enabled = !!isEnabled;
    console.log(`PerformanceMonitor: ${this.enabled ? 'habilitado' : 'desabilitado'}`);
  }
  
  /**
   * Inicia a medição de tempo para uma operação específica
   * @param {string} operationName - Nome da operação sendo medida
   * @param {Object} metadata - Metadados adicionais para contexto
   */
  startOperation(operationName, metadata = {}) {
    if (!this.enabled) return;
    
    const now = performance.now();
    this.startTimes[operationName] = now;
    this._lastOperationStart = now; // Armazena para uso em métodos que precisem do tempo de início
    
    // Registra snapshot de memória inicial se disponível
    if (window.performance && window.performance.memory) {
      this.memorySnapshots[operationName] = {
        start: {
          usedHeapSize: window.performance.memory.usedJSHeapSize,
          totalHeapSize: window.performance.memory.totalJSHeapSize,
          timestamp: now
        }
      };
    }
    
    console.log(`🔍 [PerformanceMonitor] Iniciando: ${operationName}`, metadata);
  }
  
  /**
   * Finaliza a medição de tempo para uma operação específica
   * @param {string} operationName - Nome da operação sendo medida
   * @param {Object} metadata - Metadados adicionais para contexto
   */
  endOperation(operationName, metadata = {}) {
    if (!this.enabled) return;
    if (!this.startTimes[operationName]) {
      console.warn(`PerformanceMonitor: Operação "${operationName}" não foi iniciada`);
      return;
    }
    
    const endTime = performance.now();
    const duration = endTime - this.startTimes[operationName];
    
    // Registra snapshot de memória final se disponível
    let memoryDiff = null;
    if (window.performance && window.performance.memory && this.memorySnapshots[operationName]) {
      const startMemory = this.memorySnapshots[operationName].start;
      const endMemory = {
        usedHeapSize: window.performance.memory.usedJSHeapSize,
        totalHeapSize: window.performance.memory.totalJSHeapSize,
        timestamp: endTime
      };
      
      memoryDiff = {
        usedHeapDiff: endMemory.usedHeapSize - startMemory.usedHeapSize,
        totalHeapDiff: endMemory.totalHeapSize - startMemory.totalHeapSize,
        usedHeapDiffMB: ((endMemory.usedHeapSize - startMemory.usedHeapSize) / 1024 / 1024).toFixed(2) + ' MB',
        totalHeapDiffMB: ((endMemory.totalHeapSize - startMemory.totalHeapSize) / 1024 / 1024).toFixed(2) + ' MB'
      };
      
      this.memorySnapshots[operationName].end = endMemory;
      this.memorySnapshots[operationName].diff = memoryDiff;
    }
    
    // Armazena a medição
    if (!this.measurements[operationName]) {
      this.measurements[operationName] = [];
    }
    
    this.measurements[operationName].push({
      duration,
      timestamp: Date.now(),
      metadata,
      memory: memoryDiff
    });
    
    // Log detalhado
    console.log(`⏱️ [PerformanceMonitor] Finalizado: ${operationName} - ${duration.toFixed(2)}ms`, {
      duration: duration.toFixed(2) + 'ms',
      memory: memoryDiff,
      ...metadata
    });
    
    // Limpa o tempo de início
    delete this.startTimes[operationName];
  }
  
  /**
   * Registra uma renderização de componente
   * @param {string} componentName - Nome do componente renderizado
   * @param {Object} props - Props que causaram a renderização
   */
  logRender(componentName, props = {}) {
    if (!this.enabled) return;
    
    // Inicializa contagem se necessário
    if (!this.renderCounts[componentName]) {
      this.renderCounts[componentName] = 0;
    }
    
    // Incrementa contagem
    this.renderCounts[componentName]++;
    
    // Para limitar o log, só exibimos a cada 5 renderizações ou para a primeira
    const count = this.renderCounts[componentName];
    if (count === 1 || count % 5 === 0) {
      const propsToLog = { ...props };
      
      // Remover props muito grandes ou funções para não sobrecarregar o console
      Object.keys(propsToLog).forEach(key => {
        if (typeof propsToLog[key] === 'function') {
          propsToLog[key] = '[Function]';
        } else if (
          typeof propsToLog[key] === 'object' && 
          propsToLog[key] !== null && 
          Object.keys(propsToLog[key]).length > 10
        ) {
          propsToLog[key] = `[Object com ${Object.keys(propsToLog[key]).length} chaves]`;
        }
      });
      
      console.log(`🔄 [PerformanceMonitor] Render #${count}: ${componentName}`, propsToLog);
    }
  }
  
  /**
   * Registra um evento personalizado para o monitoramento
   * @param {string} eventName - Nome do evento
   * @param {Object} data - Dados relacionados ao evento
   */
  logEvent(eventName, data = {}) {
    if (!this.enabled) return;
    
    // Armazena o timestamp para o evento
    this._lastOperationStart = this._lastOperationStart || performance.now();
    
    // Registra o evento
    const eventData = {
      timestamp: Date.now(),
      eventName,
      ...data
    };
    
    // Armazena no histórico de eventos
    if (!this.events) {
      this.events = [];
    }
    this.events.push(eventData);
    
    // Limita o número de eventos armazenados
    if (this.events.length > 100) {
      this.events = this.events.slice(-100);
    }
    
    console.log(`📊 [PerformanceMonitor] Evento: ${eventName}`, data);
  }
  
  /**
   * Obtém o uso atual de memória se disponível
   * @returns {number} Uso de memória em bytes ou -1 se não estiver disponível
   */
  getMemoryUsage() {
    if (window.performance && window.performance.memory) {
      return window.performance.memory.usedJSHeapSize;
    }
    return -1;
  }
  
  /**
   * Retorna um resumo das medições coletadas
   */
  getSummary() {
    if (!this.enabled) return { enabled: false };
    
    const summary = {
      enabled: true,
      totalRuntime: (Date.now() - this.initTime) / 1000,
      operations: {},
      renders: { ...this.renderCounts },
      config: { ...this.config },
      events: this.events || []
    };
    
    // Processa estatísticas para cada operação
    Object.keys(this.measurements).forEach(opName => {
      const opMeasurements = this.measurements[opName];
      const durations = opMeasurements.map(m => m.duration);
      
      summary.operations[opName] = {
        count: opMeasurements.length,
        averageTime: durations.reduce((sum, val) => sum + val, 0) / durations.length,
        minTime: Math.min(...durations),
        maxTime: Math.max(...durations),
        lastCall: opMeasurements[opMeasurements.length - 1].timestamp
      };
    });
    
    return summary;
  }
  
  /**
   * Força uma atualização do monitor de performance
   * Útil para atualizar métricas em painéis de diagnóstico
   */
  update() {
    if (!this.enabled) return;
    
    // Gera um evento de atualização
    this.logEvent('monitor_update_forced', {
      timestamp: Date.now(),
      operationCount: Object.keys(this.measurements).length,
      renderCount: Object.keys(this.renderCounts).length
    });
    
    console.log('🔄 [PerformanceMonitor] Atualização forçada', {
      operações: Object.keys(this.measurements).length,
      renderizações: Object.keys(this.renderCounts).length
    });
    
    // Se houver um evento customizado para atualização de métricas, dispará-lo
    if (typeof window !== 'undefined') {
      try {
        const updateEvent = new CustomEvent('performanceMonitor:update', { 
          detail: this.getSummary() 
        });
        window.dispatchEvent(updateEvent);
      } catch (error) {
        console.error('Erro ao disparar evento de atualização:', error);
      }
    }
    
    return this.getSummary();
  }
  
  /**
   * Inicia o monitoramento das renderizações para coleta mais abrangente
   */
  startMonitoringRenders() {
    console.log('🔍 [PerformanceMonitor] Iniciando monitoramento de renderizações');
    this._monitoringRenders = true;
  }
  
  /**
   * Registra o início de uma renderização de componente específico
   * @param {string} componentName - Nome do componente que está sendo renderizado
   */
  registerRender(componentName) {
    if (!this.enabled || !this._monitoringRenders) return;
    
    if (!this._activeRenders[componentName]) {
      this._activeRenders[componentName] = 0;
    }
    
    this._activeRenders[componentName]++;
    
    // Inicializa contagem total se necessário
    if (!this.renderCounts[componentName]) {
      this.renderCounts[componentName] = 0;
    }
    
    // Incrementa contagem total
    this.renderCounts[componentName]++;
    
    console.log(`🔄 [PerformanceMonitor] Render #${this.renderCounts[componentName]}: ${componentName}`);
  }
  
  /**
   * Retorna o estado atual das renderizações ativas e contagens
   */
  getRenderState() {
    return {
      activeRenders: { ...this._activeRenders },
      totalRenders: { ...this.renderCounts }
    };
  }
  
  /**
   * Limpa todas as medições coletadas
   */
  reset() {
    this.measurements = {};
    this.renderCounts = {};
    this.startTimes = {};
    this.memorySnapshots = {};
    this._activeRenders = {};
    this.initTime = Date.now();
    console.log('PerformanceMonitor: Todas as medições foram resetadas');
  }
}

// Cria instância singleton
const performanceMonitor = new PerformanceMonitor();
export default performanceMonitor;
